/**
 * A task's chat — Aaron, Oct 25, note 1.6 ("a running task chat … that log
 * by the person who typed them and the time and date it was created").
 *
 * What both sides read: how long a message may be, the shape the server
 * answers in, the words a moment is said in, and how a message's plain text
 * becomes text and links. A message is **plain text** — line breaks kept,
 * nothing else — so the one thing the client turns into markup is a link,
 * and it does that from these parts rather than from HTML.
 */
import { z } from 'zod';
import { addDays } from '../calendar.js';
/** The longest message (0145's CHECK says the same). */
export const TASK_CHAT_MAX_CHARS = 4000;
export const taskChatBodySchema = z.object({
    body: z.string().trim()
        .min(1, 'Write a message first.')
        .max(TASK_CHAT_MAX_CHARS, `A message can be up to ${TASK_CHAT_MAX_CHARS.toLocaleString('en-US')} characters.`),
});
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const clock = (minutes) => {
    const h = Math.floor(minutes / 60) % 24;
    return `${h % 12 === 0 ? 12 : h % 12}:${String(minutes % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
/**
 * When a message was written, always with its day *and* its time (Aaron:
 * "the time and date it was created"): *Today · 9:41 AM*, *Yesterday ·
 * 7:20 PM*, *Tue, Oct 20 · 4:12 PM* — and *Oct 20, 2025 · 4:12 PM* from
 * another year. `local` is the moment in the viewer's zone, `today` their day.
 */
export function chatWhenWords(local, today) {
    const time = clock(local.minutes);
    if (local.date === today)
        return `Today · ${time}`;
    if (local.date === addDays(today, -1))
        return `Yesterday · ${time}`;
    const [y, m, d] = local.date.split('-').map(Number);
    const month = `${MONTHS[m - 1]} ${d}`;
    if (local.date.slice(0, 4) !== today.slice(0, 4))
        return `${month}, ${y} · ${time}`;
    const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()];
    return `${weekday}, ${month} · ${time}`;
}
/*
 * `https://…`, `http://…`, `www.…` and an email address. Only those: a
 * `javascript:` or `data:` in somebody's message stays words, because the
 * href is built here and never copied from the text's own scheme.
 */
const LINK = /(\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,})/gi;
/* A sentence's own full stop, comma or closing bracket is not part of the link. */
const TRAILING = /[.,;:!?'"’”]+$/;
function trimLink(raw) {
    let link = raw.replace(TRAILING, '');
    /* A closing bracket the link did not open belongs to the sentence (*(see www.x.com)*). */
    while (link.endsWith(')') && (link.match(/\(/g)?.length ?? 0) < (link.match(/\)/g)?.length ?? 0)) {
        link = link.slice(0, -1).replace(TRAILING, '');
    }
    return link;
}
/** A message's text as words and links, in order; nothing in it is markup. */
export function chatParts(body) {
    const parts = [];
    let at = 0;
    for (const match of body.matchAll(LINK)) {
        const start = match.index ?? 0;
        const link = trimLink(match[0]);
        if (!link)
            continue;
        if (start > at)
            parts.push({ text: body.slice(at, start) });
        const href = link.includes('@') && !/^https?:\/\//i.test(link) && !/^www\./i.test(link)
            ? `mailto:${link}`
            : /^https?:\/\//i.test(link) ? link : `https://${link}`;
        parts.push({ text: link, href });
        at = start + link.length;
    }
    if (at < body.length)
        parts.push({ text: body.slice(at) });
    return parts;
}
/** *Chat*, *Chat · 3* — the sheet's button, and the panel's title. */
export const chatButtonWords = (count) => (count > 0 ? `Chat · ${count}` : 'Chat');
//# sourceMappingURL=task-chat.js.map